import { Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { CpExamination } from '@se/models';
import {
    ButtonClick,
    EstensaWaitingService,
    GridUtilitiesService,
    IReportWorkflowExamService,
    ModalDialogNotificationService,
    NotificationType,
    REPORT_WORKFLOW_EXAM_SERVICE_TOKEN,
} from '@se/platform';
import { themeEstensa } from '@se/se.platform.base';
import { AgGridAngular } from 'ag-grid-angular';
import { AllCommunityModule, ColDef, GridOptions } from 'ag-grid-community';
import { isEmpty } from 'lodash-es';
import { catchError, finalize, firstValueFrom, of, switchMap, tap } from 'rxjs';

export interface RemovableExamination {
    id: string;
    idReport: string;
    exam: { description: string | null; id: string };
    patient: { lastName: string; firstName: string };
}

export interface RemoveExamDialogData {
    examination: RemovableExamination;
    header: string;
}

@Component({
    selector: 'se-remove-exam',
    templateUrl: './remove-exam.component.html',
    styleUrls: ['./remove-exam.component.scss'],
    standalone: false,
})
export class RemoveExamComponent {
    @ViewChild('datagrid', { static: true })
    grid?: AgGridAngular<CpExamination>;
    agGridModuls = [AllCommunityModule];
    agGridTheme = themeEstensa;

    gridOptions = this.buildGridOptions();
    errorMessage = '';
    items$ = of(this.data.examination.idReport).pipe(
        tap(() => (this.loading = true)),
        switchMap((idReport) => {
            const fnToCall =
                this.examService.searchExaminationsToRemoveFromReport ?? this.examService.getReportExaminations;
            return fnToCall.call(this.examService, idReport);
        }),
        catchError((exception) => {
            this.errorMessage = this.lookupErrorMessage(exception);
            return of([]);
        }),
        finalize(() => (this.loading = false)),
    );
    loading = false;

    constructor(
        @Inject(REPORT_WORKFLOW_EXAM_SERVICE_TOKEN) private examService: IReportWorkflowExamService,
        @Inject(MAT_DIALOG_DATA) public data: RemoveExamDialogData,
        private dialogRef: MatDialogRef<RemoveExamComponent, string[]>,
        private gridUtils: GridUtilitiesService,
        private modalDialogNotificationService: ModalDialogNotificationService,
        private translate: TranslateService,
        private waitingService: EstensaWaitingService,
    ) {}

    canRemove(): boolean {
        return (this.grid?.api?.getSelectedRows().length ?? 0) === 1 && !this.loading;
    }

    onClose(removedIdExamination?: string[]) {
        this.loading = false;
        this.errorMessage = '';
        this.dialogRef.close(removedIdExamination);
    }

    async onRemove() {
        if (!(await this.askForConfirmation())) {
            return;
        }
        this.waitingService.isWaiting = true;

        const idExaminations = this.grid?.api.getSelectedRows().map((ex) => ex.id);
        if (!idExaminations || isEmpty(idExaminations) || !this.examService.removeExaminationsFromReport) {
            return;
        }

        this.examService
            .removeExaminationsFromReport(this.data.examination.idReport, idExaminations)
            .pipe(
                finalize(() => {
                    this.waitingService.isWaiting = false;
                }),
            )
            .subscribe({
                complete: () => {
                    this.onClose(idExaminations);
                },
                error: (exception) => {
                    this.errorMessage = this.lookupErrorMessage(exception);
                },
            });
    }

    private async askForConfirmation(): Promise<boolean> {
        const buttonClick = await firstValueFrom(
            this.modalDialogNotificationService.notify({
                buttonYes: true,
                buttonNo: true,
                buttonCancel: false,
                buttonOk: false,
                title: this.translate.instant('Warning'),
                message: this.translate.instant('Visit_RemoveExamConfirm_1'), // Gli esami selezionati verranno rimossi dal referto. Si vuole procedere?
                type: NotificationType.warning,
            }),
        );

        return buttonClick === ButtonClick.yes;
    }

    // TODO: any
    private lookupErrorMessage(exception: any) {
        if (exception && exception.error && exception.error.estensaFaultDetails) {
            if (exception.error.estensaFaultDetails.typeName === 'TooMuchResultFault') {
                return this.translate.instant(exception.error.estensaFaultDetails.typeName, {
                    p0: exception.error.estensaFaultDetails.estensaFault.count,
                    p1: exception.error.estensaFaultDetails.estensaFault.limit,
                    p2: ' ',
                });
            }
            return this.translate.instant('AssociateFailed') + ' ' + exception.message;
        }
    }

    private buildGridOptions(): GridOptions {
        return {
            rowSelection: {
                mode: 'multiRow',
            },
            suppressCellFocus: true,
            suppressLoadingOverlay: true,
            columnDefs: this.buildColumns(),
            defaultColDef: { editable: false, sortable: true, resizable: true },
            enableBrowserTooltips: true,
        };
    }

    private buildColumns(): ColDef[] {
        return [
            {
                colId: '0',
                headerName: this.translate.instant('ExamDate'),
                headerTooltip: this.translate.instant('ExamDate'),
                tooltipField: 'examinationDate',
                field: 'examinationDate',
                width: 150,
                minWidth: 120,
                valueFormatter: this.gridUtils.dateFormatter('examinationDate'),
                comparator: this.gridUtils.dateComparator,
            },
            {
                colId: '1',
                headerName: this.translate.instant('Exam'),
                headerTooltip: this.translate.instant('Exam'),
                tooltipField: 'exam.description',
                field: 'exam.description',
                width: 420,
                minWidth: 190,
            },
            {
                colId: '2',
                headerName: this.translate.instant('BusinessUnit'),
                headerTooltip: this.translate.instant('BusinessUnit'),
                tooltipField: 'businessUnit.description',
                field: 'businessUnit.description',
                width: 220,
                minWidth: 190,
            },
        ];
    }
}
